import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box, Typography, Card, CardContent, TextField, Button, Stack,
  Skeleton, Divider, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Paper, Tooltip, Badge
} from '@mui/material'
import { Edit, Delete, ArrowBack, MarkEmailRead, Check, Close } from '@mui/icons-material'
import UserNameChip from '@/components/UserNameChip'
import MediaUploader, { UploadedMedia } from '@/components/MediaUploader'
import MediaDisplay from '@/components/MediaDisplay'
import EmojiReactions from '@/components/EmojiReactions'
import { useSnackbar } from 'notistack'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/services/apiClient'
import { PrayerRequestDetail, Comment } from '@/types'
import type { ReactionGroup } from '@/types'
import { useGroupHub } from '@/hooks/useGroupHub'
import { useGroupNavigation } from '@/contexts/GroupNavigationContext'
import { buildFamilyColorMap, getFamilyColor } from '@/utils/familyColors'

export default function PrayerRequestDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [pr, setPr] = useState<PrayerRequestDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [commentText, setCommentText] = useState('')
  const [commentMedia, setCommentMedia] = useState<UploadedMedia[]>([])
  const [submittingComment, setSubmittingComment] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editAnswer, setEditAnswer] = useState('')
  const [, setIsMarkedUnread] = useState(false)
  const [isNew, setIsNew] = useState(false)
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const [editingCommentText, setEditingCommentText] = useState('')
  const { adjustPrayerBadgeCount } = useGroupNavigation()
  const [editMedia, setEditMedia] = useState<UploadedMedia[]>([])

  const loadData = useCallback(async () => {
    if (!id) return
    try {
      const data = await apiClient.get<PrayerRequestDetail>(`/api/prayerrequests/${id}`)
      setPr(data)
      setIsMarkedUnread(data.isMarkedUnread)
      setIsNew(data.isNew)
      // Auto-mark as viewed for genuinely new items (not explicitly marked unread)
      if (data.isNew && !data.isMarkedUnread) {
        try {
          await apiClient.post(`/api/prayerrequests/${id}/viewed`)
          setIsNew(false)
          adjustPrayerBadgeCount(-1)
        } catch {
          // Silently ignore - view tracking is best-effort
        }
      }
    } catch {
      enqueueSnackbar('Failed to load prayer request', { variant: 'error' })
      navigate(-1)
    } finally {
      setLoading(false)
    }
  }, [id, enqueueSnackbar, navigate, adjustPrayerBadgeCount])

  useEffect(() => { loadData() }, [loadData])

  // Real-time updates via SignalR
  const groupIdsForHub = useMemo(() => pr?.groupId ? [pr.groupId] : [], [pr?.groupId])
  useGroupHub(groupIdsForHub, {
    onCommentAdded: (data) => {
      if (data.prayerRequestId !== id) return
      setPr(prev => {
        if (!prev) return prev
        if (prev.comments.some(c => c.id === data.comment.id)) return prev
        return { ...prev, comments: [...prev.comments, data.comment] }
      })
    },
    onCommentUpdated: (data) => {
      if (data.prayerRequestId !== id) return
      setPr(prev => {
        if (!prev) return prev
        return { ...prev, comments: prev.comments.map(c => c.id === data.comment.id ? data.comment : c) }
      })
    },
    onCommentDeleted: (data) => {
      if (data.prayerRequestId !== id) return
      setPr(prev => {
        if (!prev) return prev
        return { ...prev, comments: prev.comments.filter(c => c.id !== data.commentId) }
      })
    },
    onPrayerRequestUpdated: (updated) => {
      if (updated.id !== id) return
      setPr(prev => prev ? {
        ...prev,
        title: updated.title,
        description: updated.description,
        answer: updated.answer,
      } : prev)
    },
    onReactionToggled: (data) => {
      setPr(prev => {
        if (!prev) return prev
        // Update reactions on the prayer request itself
        if (data.entityId === prev.id) {
          return { ...prev, reactions: data.reactions }
        }
        // Update reactions on a comment
        const commentIndex = prev.comments.findIndex(c => c.id === data.entityId)
        if (commentIndex >= 0) {
          const updatedComments = [...prev.comments]
          updatedComments[commentIndex] = { ...updatedComments[commentIndex], reactions: data.reactions }
          return { ...prev, comments: updatedComments }
        }
        return prev
      })
    },
  })

  const handleAddComment = async () => {
    if (!commentText.trim() || !id) return
    setSubmittingComment(true)
    try {
      const comment = await apiClient.post<Comment>(`/api/comments/prayer-request/${id}`, {
        text: commentText.trim(),
        mediaIds: commentMedia.map(u => u.id),
      })
      setPr(prev => {
        if (!prev) return null
        if (prev.comments.some(c => c.id === comment.id)) return prev
        return { ...prev, comments: [...prev.comments, comment] }
      })
      setCommentText('')
      // Backend auto-marks as viewed on comment; update local state
      if (isNew) {
        setIsNew(false)
        setIsMarkedUnread(false)
        adjustPrayerBadgeCount(-1)
      }
      setCommentMedia([])
    } catch {
      enqueueSnackbar('Failed to add comment', { variant: 'error' })
    } finally {
      setSubmittingComment(false)
    }
  }

  const handleUpdateComment = async (commentId: string) => {
    if (!editingCommentText.trim()) return
    try {
      const updated = await apiClient.put<Comment>(`/api/comments/${commentId}`, {
        text: editingCommentText.trim()
      })
      setPr(prev => {
        if (!prev) return null
        return { ...prev, comments: prev.comments.map(c => c.id === commentId ? updated : c) }
      })
      setEditingCommentId(null)
      setEditingCommentText('')
      enqueueSnackbar('Comment updated', { variant: 'success' })
    } catch {
      enqueueSnackbar('Failed to update comment', { variant: 'error' })
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('Delete this comment?')) return
    try {
      await apiClient.delete(`/api/comments/${commentId}`)
      setPr(prev => {
        if (!prev) return null
        return { ...prev, comments: prev.comments.filter(c => c.id !== commentId) }
      })
      enqueueSnackbar('Comment deleted', { variant: 'success' })
    } catch {
      enqueueSnackbar('Failed to delete comment', { variant: 'error' })
    }
  }

  const handleUpdate = async () => {
    if (!id || !editTitle.trim() || !editDescription.trim()) return
    try {
      await apiClient.put(`/api/prayerrequests/${id}`, {
        title: editTitle.trim(),
        description: editDescription.trim(),
        answer: editAnswer.trim() || null,
        mediaIds: editMedia.map(u => u.id),
      })
      setPr(prev => prev ? {
        ...prev,
        title: editTitle.trim(),
        description: editDescription.trim(),
        answer: editAnswer.trim() || undefined
      } : null)
      setEditOpen(false)
      enqueueSnackbar('Prayer request updated', { variant: 'success' })
    } catch {
      enqueueSnackbar('Failed to update', { variant: 'error' })
    }
  }

  const handleDelete = async () => {
    if (!id || !pr) return
    if (!confirm('Are you sure you want to delete this prayer request?')) return
    try {
      await apiClient.delete(`/api/prayerrequests/${id}`)
      enqueueSnackbar('Prayer request deleted', { variant: 'success' })
      navigate(`/groups/${pr.groupId}`)
    } catch {
      enqueueSnackbar('Failed to delete', { variant: 'error' })
    }
  }

  const handleToggleUnread = async () => {
    if (!id) return
    try {
      if (isNew) {
        // Mark as viewed (explicit action)
        await apiClient.post(`/api/prayerrequests/${id}/viewed`)
        setIsNew(false)
        setIsMarkedUnread(false)
        adjustPrayerBadgeCount(-1)
        enqueueSnackbar('Marked as viewed', { variant: 'success' })
      } else {
        // Mark as unread
        await apiClient.post(`/api/prayerrequests/${id}/unread`)
        setIsNew(true)
        setIsMarkedUnread(true)
        adjustPrayerBadgeCount(1)
        enqueueSnackbar('Marked as unread', { variant: 'success' })
      }
    } catch {
      enqueueSnackbar('Failed to update read status', { variant: 'error' })
    }
  }

  const isOwner = user?.userId === pr?.userId
    || (user?.familyId != null && user.familyId === pr?.familyId)

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width="70%" height={40} />
        <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={60} />
      </Box>
    )
  }

  if (!pr) return null

  // Build color map from all family/user IDs on this page (author + commenters)
  const allIds = [pr.familyId ?? pr.userId, ...pr.comments.map(c => c.familyId ?? c.userId)]
  const colorMap = buildFamilyColorMap(allIds)
  const authorColor = getFamilyColor(colorMap, pr.familyId ?? pr.userId)

  return (
    <Box sx={{ pb: 10, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate(`/groups/${pr.groupId}`)} sx={{ mr: 1 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" sx={{ flex: 1, fontWeight: 700 }}>
          {pr.title}
        </Typography>
        <Tooltip title={isNew ? 'Mark as viewed' : 'Mark as unread'}>
          <IconButton onClick={handleToggleUnread} sx={{ color: isNew ? 'success.main' : 'action.disabled' }}>
            {isNew ? (
              <Badge variant="dot" color="error" overlap="circular"
                sx={{ '& .MuiBadge-badge': { minWidth: 10, height: 10, borderRadius: '50%', top: 2, right: 2 } }}>
                <MarkEmailRead />
              </Badge>
            ) : (
              <MarkEmailRead />
            )}
          </IconButton>
        </Tooltip>
        {isOwner && (
          <Box>
            <IconButton onClick={() => {
              setEditTitle(pr.title)
              setEditDescription(pr.description)
              setEditAnswer(pr.answer || '')
              setEditMedia(
                (pr.mediaAttachments ?? []).map(a => ({
                  id: a.id,
                  url: a.url,
                  fileName: a.fileName,
                  contentType: a.contentType,
                }))
              )
              setEditOpen(true)
            }}>
              <Edit />
            </IconButton>
            <IconButton color="error" onClick={handleDelete}>
              <Delete />
            </IconButton>
          </Box>
        )}
      </Box>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{pr.description}</Typography>
          {pr.mediaAttachments && pr.mediaAttachments.length > 0 && (
            <MediaDisplay attachments={pr.mediaAttachments} />
          )}
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
            <UserNameChip
              userId={pr.userId}
              displayName={pr.userDisplayName}
              bgColor={authorColor?.bg}
              textColor={authorColor?.text}
            />
            <Typography variant="caption" color="text.secondary">
              {new Date(pr.createdDate).toLocaleDateString()}
            </Typography>
          </Box>
          <EmojiReactions
            entityId={pr.id}
            entityType="prayerrequest"
            reactions={pr.reactions}
            onReactionsChange={(reactions: ReactionGroup[]) => setPr(prev => prev ? { ...prev, reactions } : null)}
          />
        </CardContent>
      </Card>

      {/* Answer Section */}
      {pr.answer && (
        <Card variant="outlined" sx={{ mb: 2, borderColor: 'success.main', borderWidth: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="success.main" gutterBottom sx={{ fontWeight: 700 }}>
              Answered
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{pr.answer}</Typography>
          </CardContent>
        </Card>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Comments */}
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        Comments ({pr.comments.length})
      </Typography>

      {pr.comments.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {pr.comments.map(comment => {
            const commentColor = getFamilyColor(colorMap, comment.familyId ?? comment.userId)
            const isCommentOwner = user?.userId === comment.userId
            const isEditing = editingCommentId === comment.id
            return (
            <Paper key={comment.id} variant="outlined" sx={{ p: 1.5 }}>
              {isEditing ? (
                <Box>
                  <TextField
                    fullWidth size="small" multiline maxRows={4}
                    value={editingCommentText}
                    onChange={e => setEditingCommentText(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleUpdateComment(comment.id))}
                    autoFocus
                  />
                  <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    <IconButton size="small" color="primary" onClick={() => handleUpdateComment(comment.id)}
                      disabled={!editingCommentText.trim()}>
                      <Check fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => { setEditingCommentId(null); setEditingCommentText('') }}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{comment.text}</Typography>
                  {comment.mediaAttachments && comment.mediaAttachments.length > 0 && (
                    <MediaDisplay attachments={comment.mediaAttachments} />
                  )}
                  <Box sx={{ mt: 0.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <UserNameChip
                      userId={comment.userId}
                      displayName={comment.userDisplayName}
                      bgColor={commentColor?.bg}
                      textColor={commentColor?.text}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(comment.createdDate).toLocaleDateString()}
                      {comment.editedDate && ' (edited)'}
                    </Typography>
                    {isCommentOwner && (
                      <Box sx={{ ml: 'auto', display: 'flex', gap: 0.25 }}>
                        <IconButton size="small" onClick={() => { setEditingCommentId(comment.id); setEditingCommentText(comment.text) }}>
                          <Edit sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteComment(comment.id)}>
                          <Delete sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  <EmojiReactions
                    entityId={comment.id}
                    entityType="prayerrequestcomment"
                    reactions={comment.reactions}
                    onReactionsChange={(reactions: ReactionGroup[]) => setPr(prev => {
                      if (!prev) return null
                      return { ...prev, comments: prev.comments.map(c => c.id === comment.id ? { ...c, reactions } : c) }
                    })}
                  />
                </>
              )}
            </Paper>
            )
          })}
        </Stack>
      )}

      {/* Add Comment */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Add a comment..."
            value={commentText}
            onChange={e => setCommentText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleAddComment())}
            multiline
            maxRows={3}
          />
          <Button
            variant="contained"
            onClick={handleAddComment}
            disabled={submittingComment || !commentText.trim()}
            sx={{ minWidth: 80 }}
          >
            Send
          </Button>
        </Box>
        <MediaUploader
          uploads={commentMedia}
          onChange={setCommentMedia}
          disabled={submittingComment}
        />
      </Box>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Prayer Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Title" value={editTitle}
            onChange={e => setEditTitle(e.target.value)} margin="dense"
          />
          <TextField
            fullWidth label="Description" value={editDescription}
            onChange={e => setEditDescription(e.target.value)} margin="dense"
            multiline rows={3}
          />
          <TextField
            fullWidth label="Answer (optional)" value={editAnswer}
            onChange={e => setEditAnswer(e.target.value)} margin="dense"
            multiline rows={2}
            helperText="Share how this prayer was answered"
          />
          <Box sx={{ mt: 1 }}>
            <MediaUploader uploads={editMedia} onChange={setEditMedia} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained" disabled={!editTitle.trim() || !editDescription.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

